import React from "react";
import { useTranslation } from "react-i18next";
import {
  TRAINING_TEST_STEPS,
  TrainingTestStep,
  stepIndex,
} from "./training-test-steps";

interface TrainingTestStepProgressProps {
  currentStep: TrainingTestStep;
  completion: Record<TrainingTestStep, boolean>;
  onStepSelect: (step: TrainingTestStep) => void;
}

const STEP_LABEL_KEYS: Record<
  TrainingTestStep,
  { key: string; defaultValue: string }
> = {
  basics: { key: "training_step_basics", defaultValue: "Basics" },
  materials: { key: "training_step_materials", defaultValue: "Materials" },
  questions: { key: "training_step_questions", defaultValue: "Questions" },
  audience: { key: "training_step_audience", defaultValue: "Audience" },
  review: { key: "training_step_review", defaultValue: "Review" },
};

export default function TrainingTestStepProgress({
  currentStep,
  completion,
  onStepSelect,
}: TrainingTestStepProgressProps) {
  const { t } = useTranslation();
  const currentIndex = stepIndex(currentStep);

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-3 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
      <div className="flex flex-wrap items-center gap-2">
        {TRAINING_TEST_STEPS.map((step, idx) => {
          const label = t(STEP_LABEL_KEYS[step].key, {
            defaultValue: STEP_LABEL_KEYS[step].defaultValue,
          });
          const isCurrent = currentStep === step;
          const isDone = completion[step];
          return (
            <button
              key={step}
              type="button"
              onClick={() => onStepSelect(step)}
              className={`min-w-[88px] rounded-lg px-3 py-1.5 text-center transition-colors ${
                isCurrent
                  ? "bg-blue-500 text-white"
                  : isDone
                    ? "bg-emerald-500 text-white hover:bg-emerald-600"
                    : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
              }`}
            >
              <p className={`text-sm ${isCurrent ? "font-bold" : ""}`}>
                {`${idx + 1}. ${label}`}
              </p>
            </button>
          );
        })}
      </div>
      {currentIndex >= 0 ? (
        <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
          {t("training_step_progress", {
            defaultValue: "Step {{current}} of {{total}}",
            current: currentIndex + 1,
            total: TRAINING_TEST_STEPS.length,
          })}
        </p>
      ) : null}
    </div>
  );
}
